import { useCallback, useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, X } from 'lucide-react';

const CATEGORIES = ['Hosting', 'Calibration', 'LTE', 'Installation', 'Shipping'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

interface RevenueFiltersProps {
  search: string;
  category: string;
  year: number | undefined;
  month: string;
  dateFrom: string;
  dateTo: string;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onYearChange: (value: number | undefined) => void;
  onMonthChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onClearAll: () => void;
}

export function RevenueFilters({
  search,
  category,
  year,
  month,
  dateFrom,
  dateTo,
  onSearchChange,
  onCategoryChange,
  onYearChange,
  onMonthChange,
  onDateFromChange,
  onDateToChange,
  onClearAll,
}: RevenueFiltersProps) {
  const [searchInput, setSearchInput] = useState(search);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, onSearchChange]);

  // Sync external search prop
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const hasActiveFilters = !!search || !!category || year !== undefined || !!month || !!dateFrom || !!dateTo;

  const handleClearAll = useCallback(() => {
    setSearchInput('');
    onClearAll();
  }, [onClearAll]);

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/* Search */}
      <div className="relative w-64">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search accounts or invoices..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-8"
        />
      </div>

      {/* Category */}
      <Select value={category || '_all'} onValueChange={(v) => onCategoryChange(v === '_all' ? '' : v)}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Categories</SelectItem>
          {CATEGORIES.map((c) => (
            <SelectItem key={c} value={c}>{c}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Year */}
      <Select value={year?.toString() || '_all'} onValueChange={(v) => onYearChange(v === '_all' ? undefined : parseInt(v, 10))}>
        <SelectTrigger className="w-28">
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Years</SelectItem>
          <SelectItem value="2025">2025</SelectItem>
          <SelectItem value="2024">2024</SelectItem>
          <SelectItem value="2023">2023</SelectItem>
        </SelectContent>
      </Select>

      {/* Month */}
      <Select value={month || '_all'} onValueChange={(v) => onMonthChange(v === '_all' ? '' : v)}>
        <SelectTrigger className="w-28">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Months</SelectItem>
          {MONTHS.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Date From */}
      <Input
        type="date"
        value={dateFrom}
        onChange={(e) => onDateFromChange(e.target.value)}
        className="w-36"
        placeholder="From"
      />

      {/* Date To */}
      <Input
        type="date"
        value={dateTo}
        onChange={(e) => onDateToChange(e.target.value)}
        className="w-36"
        placeholder="To"
      />

      {/* Clear All */}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={handleClearAll}>
          <X className="mr-1 h-4 w-4" /> Clear
        </Button>
      )}
    </div>
  );
}
